import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ShieldCheck, Building2, IdCard, Check, X, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

export default function AdminVerifications() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [pending, setPending] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const isAdmin = user?.userType === "admin";
  useEffect(() => { if (!loading && !isAdmin) navigate("/"); }, [loading, isAdmin, navigate]);
  useEffect(() => { if (isAdmin) api.pendingVerifications().then((p) => setPending(p)).catch(() => setPending([])); }, [isAdmin]);

  if (loading || !isAdmin) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  async function refresh() { setPending(await api.pendingVerifications().catch(() => [])); }
  async function approve(id: string) {
    setBusy(id + "a"); try { await api.approveVerification(id); await refresh(); } catch { /* ignore */ } finally { setBusy(null); }
  }
  async function doReject(id: string) {
    setBusy(id + "r"); try { await api.rejectVerification(id, note.trim() || undefined); setRejectId(null); setNote(""); await refresh(); } catch { /* ignore */ } finally { setBusy(null); }
  }

  return (
    <div className="container-x py-6">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-6 h-6 text-brand-green" />
        <h1 className="text-2xl font-extrabold text-navy">Verification Queue</h1>
      </div>
      <p className="text-mut text-sm mb-6">Review and approve identity &amp; company verification requests.</p>

      {!pending ? (
        <div className="py-16 text-center text-mut"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : pending.length === 0 ? (
        <div className="card p-10 text-center">
          <ShieldCheck className="w-8 h-8 text-mut mx-auto mb-2" />
          <div className="font-bold text-navy">Queue is clear</div>
          <p className="text-sm text-mut mt-1">No pending verification requests right now.</p>
        </div>
      ) : (
        <div className="space-y-3 max-w-3xl">
          {pending.map((p) => (
            <div key={p.id} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0">
                    {p.kind === "company" ? <Building2 className="w-5 h-5" /> : <IdCard className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <Link href={`/u/${p.userId}`} className="font-bold text-navy hover:text-brand-red">{p.userName}</Link>
                    <div className="text-xs text-mut">{p.email}</div>
                    <div className="text-sm text-navy-700 mt-1">
                      <span className="chip bg-navy-50 text-navy-600 capitalize mr-2">{p.kind === "company" ? "Company" : "Identity"}</span>
                      {p.label || <span className="text-mut">No label provided</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => approve(p.id)} disabled={busy === p.id + "a"} className="btn-navy h-9 px-3 text-xs disabled:opacity-50"><Check className="w-3.5 h-3.5" />Approve</button>
                  <button onClick={() => { setRejectId(rejectId === p.id ? null : p.id); setNote(""); }} className="btn-outline h-9 px-3 text-xs text-brand-red"><X className="w-3.5 h-3.5" />Reject</button>
                </div>
              </div>
              {rejectId === p.id && (
                <div className="mt-3 pt-3 border-t border-line flex gap-2">
                  <input className="field flex-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional) — shown to the user" />
                  <button onClick={() => doReject(p.id)} disabled={busy === p.id + "r"} className="btn-red h-10 px-3 text-xs disabled:opacity-50">Confirm reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
